import React, { ForwardedRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Accept, FileRejection, FileWithPath, useDropzone } from 'react-dropzone';
import { lookup } from 'mime-types';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Button, SingleLineText, CardItem, dsV2 } from '@bloomays-lib/ui.shared';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

export const FILE_INVALID_TYPE = 'file-invalid-type';
export const FILE_TOO_LARGE = 'file-too-large';
export const FILE_TOO_SMALL = 'file-too-small';
export const TOO_MANY_FILES = 'too-many-files';

const bytesToSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(`${Math.floor(Math.log(bytes) / Math.log(1024))}`);
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

export type DropzoneProps = {
  id: string;
  fileTypes: string[];
  onUpload: (id: string, acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  isLoading: boolean;
  title?: string;
  placeholder?: string;
  maxFiles: number;
  maxLength: number;
  autoload: boolean;
  noDrag?: boolean;
  showFiles?: boolean;
  /** URL du document déjà déposé — affiche un aperçu inline (œil) dans la variante compacte (`showFiles={false}`). */
  previewUrl?: string;
  /** Callback de l'aperçu inline (variante compacte). */
  onPreview?: () => void;
};

const Dropzone = forwardRef(
  (
    {
      id,
      onUpload,
      isLoading,
      fileTypes,
      title,
      maxFiles = 1,
      maxLength = 20000000,
      placeholder,
      autoload,
      noDrag = false,
      showFiles = true,
      previewUrl,
      onPreview,
    }: DropzoneProps,
    ref: ForwardedRef<{ open: () => void }>,
  ) => {
    const theme = useTheme();
    const { t } = useTranslation('random');

    const baseStyle = {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      borderWidth: 4,
      borderRadius: 20,
      borderColor: theme.palette.neutral[20],
      borderStyle: 'dashed',
      backgroundColor: theme.palette.neutral[10],
      outline: 'none',
      transition: 'border .24s ease-in-out',
      cursor: 'pointer',
    };

    const activeStyle = {
      borderColor: theme.palette.blue[60],
    };

    const acceptStyle = {
      borderColor: theme.palette.green[80],
    };

    const rejectStyle = {
      borderColor: theme.palette.red[80],
    };
    const onDrop = <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      if (autoload && onUpload) {
        onUpload(id, acceptedFiles, i18n(fileRejections));
      }
    };

    const i18n = (fileRejections: readonly FileRejection[]) => {
      return fileRejections.map((fileRejection: FileRejection) => {
        fileRejection.errors = fileRejection.errors.map((e: { code: string; message: string }) => {
          switch (e.code) {
            case FILE_INVALID_TYPE:
              e.message = `le type de fichier doit être ${fileTypes.join(', ')}`;
              break;
            case FILE_TOO_LARGE:
              e.message = `Le fichier ne doit pas dépasser ${bytesToSize(maxLength)}`;
              break;
            case FILE_TOO_SMALL:
              e.message = `Le fichier doit pas dépasser un poids de ${bytesToSize(maxLength)}`;
              break;

            case TOO_MANY_FILES:
              e.message = `Maximum ${maxFiles} fichier${maxFiles > 1 ? 's' : ''}`;
              break;

            default:
              break;
          }
          return e;
        });
        return fileRejection;
      });
    };

    const accept: Accept = {};
    fileTypes.forEach((fileType) => {
      const mimeType = lookup(fileType);
      if (!mimeType) {
        throw new Error(`Unknown mime type ${fileType}`);
      }
      if (accept[mimeType]) {
        accept[mimeType] = [...accept[mimeType], fileType];
      } else {
        accept[mimeType] = [fileType];
      }
    });

    const {
      acceptedFiles,
      getRootProps,
      getInputProps,
      fileRejections,
      isFocused,
      isDragAccept,
      isDragReject,
      open: openDZ,
    } = useDropzone({
      accept: accept,
      maxFiles,
      disabled: isLoading,
      maxSize: maxLength,
      onDrop,
      noDrag,
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useImperativeHandle(ref, () => ({
      open() {
        return openDZ();
      },
    }));

    const acceptedFileItems = acceptedFiles.map((file: FileWithPath) => (
      <li key={file.path}>
        <SingleLineText text={`${file.path} - `} span={`${file.size} bytes`} />
      </li>
    ));

    const fileRejectionItems = i18n(fileRejections).map(({ file, errors }) => (
      <li key={file.path}>
        <SingleLineText text={`${file.path} - `} span={`${file.size} bytes`} />
        <ul>
          {errors.map(
            (e: { code: React.Key | null | undefined; message: boolean | React.ReactNode | null | undefined }) => (
              <li key={e.code}>{e.message}</li>
            ),
          )}
        </ul>
      </li>
    ));

    const style = useMemo(
      () => ({
        ...baseStyle,
        ...(isFocused ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isFocused, isDragReject, isDragAccept],
    );

    // Variante compacte (`showFiles={false}`) : contrôle slim « Remplacer / Uploader un fichier »
    // + aperçu inline (œil). Sélectionner un fichier (autoload) le remonte directement via `onUpload`.
    if (!showFiles) {
      const hasNewFile = acceptedFiles.length > 0;
      const isReplace = hasNewFile || Boolean(previewUrl);
      return (
        <Stack direction="row" spacing={1} alignItems="stretch">
          <Box
            {...getRootProps()}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              padding: '9px 14px',
              borderRadius: dsV2.radius.sm,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: isLoading ? 'progress' : 'pointer',
              transition: 'filter .15s, border-color .15s',
              outline: 'none',
              opacity: isLoading ? 0.6 : 1,
              pointerEvents: isLoading ? 'none' : 'auto',
              ...(isReplace
                ? {
                    background: dsV2.color.vertDim,
                    color: dsV2.color.vert,
                    border: `1px solid ${dsV2.color.vertBrd}`,
                  }
                : {
                    background: dsV2.color.surface,
                    color: dsV2.color.bleu,
                    border: `1.5px dashed ${isDragAccept ? dsV2.color.bleu : dsV2.color.bleuBrd}`,
                  }),
              '&:hover': { filter: 'brightness(0.985)' },
            }}
          >
            <input {...getInputProps()} />
            {isLoading ? (
              <CircularProgress size={15} sx={{ color: 'inherit' }} />
            ) : isReplace ? (
              <AutorenewIcon sx={{ fontSize: 16 }} />
            ) : (
              <UploadFileIcon sx={{ fontSize: 16 }} />
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {isReplace ? t('textDropzoneReplace') : t('uploadFile')}
            </span>
          </Box>
          {previewUrl && onPreview ? (
            <Box
              component="button"
              type="button"
              aria-label="document-preview"
              onClick={onPreview}
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                borderRadius: dsV2.radius.sm,
                border: `1px solid ${dsV2.color.border}`,
                background: dsV2.color.surface,
                color: dsV2.color.t3,
                cursor: 'pointer',
                '&:hover': { background: dsV2.color.surface2 },
              }}
            >
              <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
          ) : null}
        </Stack>
      );
    }

    return (
      <div>
        <div {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <StyledContainerTextEmoji>
            {acceptedFiles.length === 0 ? (
              <>
                <CardItem variant={'blue'} size={'small'} rounded={true} emoji={<UploadFileIcon />} />
                <div>
                  <SingleLineText text={t('textDropzone')} />
                  <SingleLineText
                    text={`${fileTypes.join(', ')} (max ${bytesToSize(maxLength)})`}
                    variant={'body1Light'}
                  />
                </div>
              </>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <CardItem variant={'blue'} size={'small'} rounded={true} emoji={<AutorenewIcon />} />
                <SingleLineText text={t('textDropzoneReplace')} />
              </Stack>
            )}
          </StyledContainerTextEmoji>
        </div>
        {showFiles && (
          <div className="containerUploadValidator">
            {acceptedFileItems.length > 0 && (
              <aside>
                <h4>Fichier ajouté :</h4>
                <ul>{acceptedFileItems}</ul>
              </aside>
            )}
            {fileRejectionItems.length > 0 && (
              <aside>
                <h4>Fichier rejeté : </h4>
                <ul className="red">{fileRejectionItems}</ul>
              </aside>
            )}
            {!autoload && (
              <Button
                textButton={title || ''}
                onClick={() => onUpload(id, Array.from(acceptedFiles), Array.from(fileRejections || []))}
                disable={isLoading}
                title={title}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

export default Dropzone;

export { Dropzone };

const StyledContainerTextEmoji = styled('div')(
  () => `
  display: flex;
  flex-flow: row nowrap;
  gap: 12px;
  `,
);
